import type {
  ActionsResponse,
  StudentAction,
  StudentActionTableRow,
} from '@/domains/academicRisk/academicRiskAction.entity';
import type { AcademicRiskActionRepository } from '@/domains/academicRisk/academicRiskAction.repository';

/**
 * Use Case para obtener y procesar las acciones de un estudiante
 *
 * Responsabilidades:
 * - Obtener acciones del estudiante desde el repositorio
 * - Transformar datos para UI (aplanar incorporaciones)
 * - Ordenar por fecha descendente
 */
export class GetAcademicRiskActionUseCase {
  private repository: AcademicRiskActionRepository;

  constructor(repository: AcademicRiskActionRepository) {
    this.repository = repository;
  }

  /**
   * Ejecuta la obtención de acciones del estudiante
   * @param cod_incorporacion_estudiante - Código de incorporación del estudiante
   */
  async execute(
    cod_incorporacion_estudiante: string,
  ): Promise<StudentActionTableRow[]> {
    const response: ActionsResponse = await this.repository.getActions(
      cod_incorporacion_estudiante,
    );

    const processedActions = this.transformActionsForTable(response.acciones);

    return this.sortByDateDesc(processedActions);
  }

  /**
   * Transforma las acciones de la API a formato de tabla
   * Aplana las incorporaciones para mostrar cada una como fila
   */
  private transformActionsForTable(
    actions: StudentAction[],
  ): StudentActionTableRow[] {
    const tableRows: StudentActionTableRow[] = [];

    for (const action of actions) {
      if (action.incorporaciones && action.incorporaciones.length > 0) {
        for (const incorporacion of action.incorporaciones) {
          tableRows.push({
            cod_accion: action.cod_accion,
            fecha: incorporacion.fecha,
            responsable: incorporacion.funcionario_accion,
            cod_riesgo: action.cod_riesgo,
            cod_tipo_accion: action.cod_tipo_accion,
            desc_tipo_accion: action.desc_tipo_accion,
            estado_accion: incorporacion.estado_accion,
            observaciones: incorporacion.desc_incorporacion_accion,
            acuerdos: incorporacion.acuerdo,
            cod_incorporacion_accion: incorporacion.cod_incorporacion_accion,
          });
        }
      }
    }

    return tableRows;
  }

  /**
   * Ordena las acciones por fecha descendente (más reciente primero)
   */
  private sortByDateDesc(
    actions: StudentActionTableRow[],
  ): StudentActionTableRow[] {
    return actions.sort((a, b) => {
      const dateA = this.parseDate(a.fecha);
      const dateB = this.parseDate(b.fecha);
      return dateB.getTime() - dateA.getTime();
    });
  }

  /**
   * Convierte fecha en formato dd-mm-yy a Date
   */
  private parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('-').map(Number);
    const fullYear = year < 100 ? 2000 + year : year;
    return new Date(fullYear, month - 1, day);
  }
}
